import { NINJAS, STARTER_NINJA, ninjaById, type Ninja } from "../content/ninjas";

/*
  The roster: which ninjas the child has recruited, when, and their battle
  record against each. Seedling is on every roster from the start. A ninja is
  recruited by beating it in battle, once; the fold does the recruiting so
  every device agrees. Pure.
*/

export interface RosterEntry {
  /** ISO date recruited; missing while the ninja has only been fought. */
  recruited?: string | undefined;
  fought: number;
  won: number;
}
export type Roster = Record<string, RosterEntry>;

export function isRecruited(roster: Roster, id: string): boolean {
  return id === STARTER_NINJA || !!roster[id]?.recruited;
}

/** Recruited ninjas, lowest rank first. */
export function recruited(roster: Roster): Ninja[] {
  return NINJAS.filter((n) => isRecruited(roster, n.id));
}

/** The ninja the child fights with: their choice if recruited, else the best recruited. */
export function fighterFor(roster: Roster, choice: string | undefined): Ninja {
  if (choice && isRecruited(roster, choice)) {
    const n = ninjaById(choice);
    if (n) return n;
  }
  const all = recruited(roster);
  return all[all.length - 1] ?? ninjaById(STARTER_NINJA)!;
}

/** The lowest-rank ninja not yet recruited, or null when the roster is full. */
export function nextToRecruit(roster: Roster): Ninja | null {
  return NINJAS.find((n) => !isRecruited(roster, n.id)) ?? null;
}

/** The record against one ninja. */
export function recordAgainst(roster: Roster, id: string): { fought: number; won: number } {
  const r = roster[id];
  return { fought: r?.fought ?? 0, won: r?.won ?? 0 };
}

/**
 * Fold a battle into the roster: fought, won, and the recruit date the first
 * time the ninja is beaten. Never removes anything.
 */
export function recordBattle(roster: Roster, id: string, won: boolean, day: string): Roster {
  const had = roster[id];
  const date = had?.recruited ?? (won ? day : undefined);
  return {
    ...roster,
    [id]: {
      ...(date ? { recruited: date } : {}),
      fought: (had?.fought ?? 0) + 1,
      won: (had?.won ?? 0) + (won ? 1 : 0),
    },
  };
}

const earliest = (a: string | undefined, b: string | undefined) =>
  a && b ? (a < b ? a : b) : (a ?? b);

/** Merge two rosters: the earliest recruit date, the counts added. */
export function mergeRosters(a: Roster, b: Roster): Roster {
  const out: Roster = { ...a };
  for (const [id, r] of Object.entries(b)) {
    const had = out[id];
    const date = earliest(had?.recruited, r.recruited);
    out[id] = {
      ...(date ? { recruited: date } : {}),
      fought: (had?.fought ?? 0) + r.fought,
      won: (had?.won ?? 0) + r.won,
    };
  }
  return out;
}

/** Whether the child has met this ninja: holds its rank in any strand, or has recruited it. */
export function isKnown(rank: Record<string, number>, roster: Roster, n: Ninja): boolean {
  return isRecruited(roster, n.id) || Object.values(rank).some((r) => r >= n.rankIndex);
}
